// src/sectors/pilates/sessions/components/calendar/DesktopCalendar.jsx
import React, { useMemo, useState } from 'react';
import Divider from '@mui/material/Divider';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

import MDBox from 'components/MDBox';
import { CalendarHeaderRow } from './CalendarHeaderRow';
import { TimeCell } from './TimeCell';
import { DayCell } from './DayCell';
import { sessionsApi } from 'sectors/pilates/sessions/api/sessionsApi';
import { ConfirmDialog } from 'sectors/pilates/sessions/modal/parts/ConfirmDialog';
import { ModeFab } from './ModeFab';

// ... üst importlar aynı

export function DesktopCalendar(props) {
  const {
    days = [],
    slots = [],
    today,
    trainersById,
    byDaySlot,
    onSessionEdit,
    onSessionDelete,
    onSessionClick,
    onEmptyClick,
    editMode = false,
    moveMode,
    setMoveMode,
    moveFrom,
    setMoveFrom,
    ghostByDaySlot,
    ghostMode = false,
    attendanceMode = false,
    onAttendanceClick,
    filterTrainerId = 'ALL',
    sessionsForCheck = null,
    onMoveSession,

    timeColWidth = 86,
    minCellHeight = 64,
    cellPad = 6,
    cellGap = 6,
    copyMode = false,
  } = props;

  const theme = useMuiTheme();

  // ✅ HOOKS: en üstte
  const gridCols = useMemo(() => `${timeColWidth}px repeat(7, minmax(0, 1fr))`, [timeColWidth]);

  const [blockConflict, setBlockConflict] = useState(null);
  const [confirmWarn, setConfirmWarn] = useState(null); // ✅ warning modal (backend veya local)
  const [confirmDefault, setConfirmDefault] = useState(false); // ✅ warning yoksa da sor
  const [pendingMove, setPendingMove] = useState(null);

  const [moveBusy, setMoveBusy] = useState(false);
  const [lastDecision, setLastDecision] = useState(null);

  const allSessions = useMemo(() => {
    if (Array.isArray(sessionsForCheck)) return sessionsForCheck;
    const out = [];
    if (byDaySlot?.forEach) {
      byDaySlot.forEach((arr) => {
        if (Array.isArray(arr)) out.push(...arr);
      });
    }
    return out;
  }, [byDaySlot, sessionsForCheck]);

  // --------- helpers ----------
  const getSessionId = (s) => String(s?.id ?? s?.sessionId ?? '');
  const getTrainerId = (s) => String(s?.trainerId ?? '');
  const getMemberId = (p) => p?.memberId ?? p?.member?.id ?? null;
  const getMemberName = (p) =>
    (p?.fullName || p?.memberNameSnapshot || p?.memberName || '').toString().trim();

  const getMembersMap = (s) => {
    const ps = Array.isArray(s?.participants) ? s.participants : [];
    const m = new Map();
    for (const p of ps) {
      const id = getMemberId(p);
      if (id == null) continue;
      const name = getMemberName(p) || `#${id}`;
      m.set(String(id), name);
    }
    return m;
  };

  const normDay = (x) => (x ? String(x).slice(0, 10) : '');

  const sessionsAtSlot = (allSessions, dayISO, startMin) =>
    (allSessions || []).filter(
      (x) => String(x?.dayISO) === String(dayISO) && Number(x?.startMin) === Number(startMin)
    );

  const formatNames = (members = []) => {
    const names = (members || []).map((x) => x?.name).filter(Boolean);
    if (!names.length) return '';
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} ve ${names[1]}`;
    return `${names.slice(0, 2).join(', ')} ve ${names.length - 2} kişi`;
  };

  const decisionToBlock = (d) => {
    const b = d?.blocks?.[0];
    if (!b) return null;
    return { ok: false, kind: 'BLOCK', reason: b.code, message: b.message || 'Taşıma engellendi.' };
  };

  const decisionToWarning = (d) => {
    const ws = Array.isArray(d?.warnings) ? d.warnings : [];
    if (!ws.length) return null;
    const msg = ws.length === 1 ? ws[0].message : ws.map((x) => `• ${x.message}`).join('\n');
    return { ok: false, kind: 'CONFIRM', reason: ws[0]?.code || 'WARNING', message: msg };
  };

  function checkHardConflicts({ allSessions, movingSession, to }) {
    const movingId = getSessionId(movingSession);
    const movingTrainerId = getTrainerId(movingSession);

    const movingMembersMap = getMembersMap(movingSession);
    const movingMemberIds = new Set([...movingMembersMap.keys()]);

    const atTarget = sessionsAtSlot(allSessions, to.dayISO, to.startMin).filter(
      (x) => getSessionId(x) !== movingId
    );

    const trainerHit = atTarget.find((x) => getTrainerId(x) && getTrainerId(x) === movingTrainerId);
    if (trainerHit) {
      return {
        ok: false,
        kind: 'BLOCK',
        reason: 'TRAINER_SLOT',
        message: 'Buraya taşıyamazsın: bu saatte eğitmenin başka bir seansı var.',
      };
    }

    if (movingMemberIds.size) {
      const hitNames = new Map();
      for (const x of atTarget) {
        const targetMembers = getMembersMap(x);
        for (const mid of movingMemberIds) {
          if (targetMembers.has(mid)) {
            hitNames.set(mid, movingMembersMap.get(mid) || targetMembers.get(mid) || `#${mid}`);
          }
        }
      }
      if (hitNames.size) {
        const members = [...hitNames.entries()].map(([id, name]) => ({ id, name }));
        return {
          ok: false,
          kind: 'BLOCK',
          reason: 'MEMBER_SLOT',
          members,
          message: `Buraya taşıyamazsın: bu saatte ${formatNames(
            members
          )} zaten başka bir seansta.`,
        };
      }
    }

    return { ok: true };
  }

  function checkSameDayWarning({ allSessions, movingSession, to }) {
    const movingId = getSessionId(movingSession);

    const movingMembersMap = getMembersMap(movingSession);
    const movingMemberIds = new Set([...movingMembersMap.keys()]);
    if (!movingMemberIds.size) return { ok: true };

    const sameDayOtherSessions = (allSessions || []).filter((x) => {
      if (getSessionId(x) === movingId) return false;
      return normDay(x?.dayISO) === normDay(to.dayISO);
    });

    const hitNames = new Map();
    for (const x of sameDayOtherSessions) {
      const targetMembers = getMembersMap(x);
      for (const mid of movingMemberIds) {
        if (targetMembers.has(mid)) {
          hitNames.set(mid, movingMembersMap.get(mid) || targetMembers.get(mid) || `#${mid}`);
        }
      }
    }

    if (!hitNames.size) return { ok: true };

    const members = [...hitNames.entries()].map(([id, name]) => ({ id, name }));

    return {
      ok: false,
      kind: 'CONFIRM',
      reason: 'MEMBER_SAMEDAY',
      members,
      message:
        members.length === 1
          ? `${members[0].name} bugün zaten başka bir seansa sahip. Emin misin?`
          : `Bugün zaten başka seansı olan üyeler var (${formatNames(members)}). Emin misin?`,
    };
  }

  const handleMoveRequest = async (payload) => {
    console.log('[move] payload=', payload);

    if (moveBusy) return;

    const sid = String(payload?.sessionId ?? '');
    if (!sid) return;

    const movingSession = allSessions.find((x) => String(x?.id ?? x?.sessionId ?? '') === sid);

    const toRaw = payload?.to ?? payload ?? {};
    const to = {
      dayISO: String(
        toRaw?.dayISO ?? toRaw?.toDayISO ?? toRaw?.targetDayISO ?? toRaw?.day ?? toRaw?.date ?? ''
      ).slice(0, 10),
      startMin: Number(
        toRaw?.startMin ??
          toRaw?.toStartMin ??
          toRaw?.targetStartMin ??
          toRaw?.minute ??
          toRaw?.min ??
          NaN
      ),
    };
    console.log('[move] parsed to=', to, 'sid=', sid);
    if (!to.dayISO || !Number.isFinite(to.startMin)) return;

    const safePayload = {
      sessionId: Number(sid),
      from: payload?.from
        ? payload.from
        : movingSession
        ? {
            dayISO: String(movingSession.dayISO).slice(0, 10),
            startMin: Number(movingSession.startMin),
          }
        : undefined,
      to,
    };

    // local UX checks
    if (movingSession) {
      const movingId = getSessionId(movingSession);
      const allMinusMoving = (allSessions || []).filter((x) => getSessionId(x) !== movingId);

      const hardLocal = checkHardConflicts({ allSessions: allMinusMoving, movingSession, to });
      if (!hardLocal.ok) {
        setBlockConflict(hardLocal);
        return;
      }
      const warnLocal = checkSameDayWarning({ allSessions, movingSession, to });
      if (!warnLocal.ok) {
        setConfirmWarn(warnLocal);
        setPendingMove(safePayload);
        return;
      }
    }

    try {
      setMoveBusy(true);
      const decision = await sessionsApi.moveValidate(safePayload);
      setLastDecision(decision);

      const block = decisionToBlock(decision);
      if (block) {
        setBlockConflict(block);
        return;
      }

      const warn = decisionToWarning(decision);
      if (warn) {
        setConfirmWarn(warn);
        setPendingMove(safePayload);
        return;
      }

      // ✅ warning yoksa bile "emin misin?" sor
      setPendingMove(safePayload);
      setConfirmDefault(true);
      return;
    } catch (e) {
      const msg = e?.message || 'Taşıma sırasında hata oluştu.';
      setBlockConflict({ ok: false, kind: 'BLOCK', reason: 'ERROR', message: msg });
    } finally {
      setMoveBusy(false);
    }
  };

  // ... return JSX (senin aynı)

  return (
    <MDBox
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}
    >
      <CalendarHeaderRow
        days={days}
        today={today}
        gridCols={gridCols}
        timeColWidth={timeColWidth}
      />
      <Divider />

      <MDBox
        sx={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <MDBox
          sx={{
            display: 'grid',
            gridTemplateColumns: gridCols,
            gridAutoRows: `minmax(${minCellHeight}px, auto)`,
            contentVisibility: 'auto',
            containIntrinsicSize: '800px',
            background: 'transparent',
            minWidth: 0,
          }}
        >
          {slots.map((m, rowIdx) => {
            const startMin = Number(m);
            return (
              <React.Fragment key={`row-${m}`}>
                <TimeCell
                  label={m}
                  rowIdx={rowIdx}
                  timeColWidth={timeColWidth}
                  borderColor={theme.palette.divider}
                />

                {days.map((dayISO, colIdx) => {
                  const key = `${dayISO}|${startMin}`;
                  const list = byDaySlot?.get?.(key) || [];
                  const isToday = dayISO === today;
                  const ghostList = ghostMode ? ghostByDaySlot?.get?.(key) || [] : [];

                  return (
                    <DayCell
                      key={`c-${dayISO}-${m}`}
                      dayISO={dayISO}
                      startMin={startMin}
                      colIdx={colIdx}
                      rowIdx={rowIdx}
                      isToday={isToday}
                      list={list}
                      trainersById={trainersById}
                      onSessionClick={onSessionClick}
                      onSessionEdit={onSessionEdit}
                      onSessionDelete={onSessionDelete}
                      onMoveSession={handleMoveRequest}
                      editMode={editMode}
                      onEmptyClick={onEmptyClick}
                      attendanceMode={attendanceMode}
                      onAttendanceClick={onAttendanceClick}
                      filterTrainerId={filterTrainerId}
                      copyMode={copyMode}
                      minCellHeight={minCellHeight}
                      cellPad={cellPad}
                      cellGap={cellGap}
                      moveMode={moveMode}
                      setMoveMode={setMoveMode}
                      moveFrom={moveFrom}
                      setMoveFrom={setMoveFrom}
                      ghostList={ghostList}
                      ghostMode={ghostMode}
                    />
                  );
                })}
              </React.Fragment>
            );
          })}
        </MDBox>
        <ConfirmDialog
          open={Boolean(blockConflict)}
          tone="error"
          title="Çakışma"
          subtitle="Bu işlem engellendi"
          message={[
            blockConflict?.message + '  (Takvim tipini değiştirip deneyin)' || 'Taşıma engellendi.',
            blockConflict?.members?.length
              ? '\n\nEtkilenen üyeler:\n' +
                blockConflict.members.map((m) => `• ${m.name}`).join('\n')
              : '',
          ].join('')}
          confirmText="Tamam"
          hideCancel
          onCancel={() => setBlockConflict(null)}
          onConfirm={() => setBlockConflict(null)}
        />
        <ConfirmDialog
          open={Boolean(confirmWarn)}
          tone="warning"
          title="Emin misin?"
          subtitle="Uyarı"
          busy={moveBusy}
          message={[
            confirmWarn?.message || 'Bu işlem için onay gerekiyor.',
            confirmWarn?.members?.length
              ? '\n\nÜyeler:\n' + confirmWarn.members.map((m) => `• ${m.name}`).join('\n')
              : '',
          ].join('')}
          cancelText="Vazgeç"
          confirmText={moveBusy ? 'Taşınıyor...' : 'Evet, taşı'}
          onCancel={() => {
            if (moveBusy) return;
            setConfirmWarn(null);
            setConfirmDefault(false);
            setPendingMove(null);
          }}
          onConfirm={async () => {
            const p = pendingMove;

            // ✅ önce modalları kapat (tek modal kalsın)
            setConfirmWarn(null);
            setConfirmDefault(false);
            setPendingMove(null);

            if (!p) return;

            try {
              setMoveBusy(true);

              // ✅ validate tekrar (race)
              const decision = await sessionsApi.moveValidate(p);
              setLastDecision(decision);

              const block = decisionToBlock(decision);
              if (block) {
                setBlockConflict(block);
                return;
              }

              // ✅ commit
              await sessionsApi.moveCommit(p);

              // ✅ parent refresh
              await Promise.resolve(onMoveSession?.(p));
            } catch (e) {
              const msg = e?.message || 'Taşıma sırasında hata oluştu.';
              setBlockConflict({ ok: false, kind: 'BLOCK', reason: 'ERROR', message: msg });
            } finally {
              setMoveBusy(false);
            }
          }}
        />
        <ConfirmDialog
          open={Boolean(confirmDefault)}
          tone="info"
          title="Seansı taşı"
          subtitle="Lütfen onayla"
          busy={moveBusy}
          message="Bu seansı taşımak istiyor musun?"
          cancelText="Vazgeç"
          confirmText={moveBusy ? 'Taşınıyor...' : 'Evet, taşı'}
          onCancel={() => {
            if (moveBusy) return;
            setConfirmDefault(false);
            setPendingMove(null);
          }}
          onConfirm={async () => {
            const p = pendingMove;

            setConfirmDefault(false);
            setPendingMove(null);

            if (!p) return;

            try {
              setMoveBusy(true);

              // ✅ validate tekrar (race)
              const decision = await sessionsApi.moveValidate(p);
              setLastDecision(decision);

              const block = decisionToBlock(decision);
              if (block) {
                setBlockConflict(block);
                return;
              }

              // ✅ warnings geldiyse warning modal aç (default kapanmış olacak)
              const warn = decisionToWarning(decision);
              if (warn) {
                setConfirmWarn(warn);
                setPendingMove(p);
                return;
              }

              await sessionsApi.moveCommit(p);
              await Promise.resolve(onMoveSession?.(p));
            } catch (e) {
              const msg = e?.message || 'Taşıma sırasında hata oluştu.';
              setBlockConflict({ ok: false, kind: 'BLOCK', reason: 'ERROR', message: msg });
            } finally {
              setMoveBusy(false);
            }
          }}
        />
        <ModeFab
          copyMode={copyMode}
          moveMode={moveMode}
          onExitCopy={() => props.onCopyCancel?.()}
          onExitMove={() => {
            setMoveMode(false);
            setMoveFrom(null);
          }}
        />
      </MDBox>
    </MDBox>
  );
}
